import React, { useMemo, useState } from "react";
import {
  X,
  Copy,
  Check,
  Play,
  MagnifyingGlass,
  CaretDown,
  CaretRight,
  BookOpen,
} from "@phosphor-icons/react";
import { CARDANO_NETWORK } from "@/lib/constants";

// -------- data catalog (mirrors the backend's endpoints) --------
const ENDPOINT_GROUPS = [
  {
    group: "Cardano » Accounts",
    identifier: "stake_address",
    endpoints: [
      { method: "GET", path: "/api/accounts/{stake_address}", desc: "Account summary (balance, rewards, withdrawals, pool, drep)." },
      { method: "GET", path: "/api/accounts/{stake_address}/rewards", desc: "Reward history per epoch.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/history", desc: "Active-stake history by epoch.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/delegations", desc: "Delegation certificate history.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/registrations", desc: "(De)registration history.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/withdrawals", desc: "Withdrawals from the rewards account.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/mirs", desc: "MIR (reserves/treasury) transfers.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/addresses", desc: "All addresses linked to this stake key.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/addresses/assets", desc: "Aggregated native-asset balances.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/addresses/total", desc: "Lifetime received/sent and tx count." },
      { method: "GET", path: "/api/accounts/{stake_address}/utxos", desc: "Live UTXOs of all linked addresses.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/transactions", desc: "Transactions touching this account.", paginated: true, rangeFilter: true },
      { method: "GET", path: "/api/accounts/{stake_address}/stake_history", desc: "Active-stake amount held per epoch.", paginated: true },
      { method: "GET", path: "/api/accounts/{stake_address}/drep_delegations", desc: "DRep delegation (vote-delegation) history for this stake account.", paginated: true },
    ],
  },
  {
    group: "Cardano » Addresses",
    identifier: "address",
    endpoints: [
      { method: "GET", path: "/api/addresses/{address}", desc: "Address summary (balance, stake key, type, script flag)." },
      { method: "GET", path: "/api/addresses/{address}/info", desc: "Address info — balance, stake key, address type and script flag." },
      { method: "GET", path: "/api/addresses/{address}/extended", desc: "Summary + CIP-25 decimals and NFT on-chain metadata flag." },
      { method: "GET", path: "/api/addresses/{address}/total", desc: "Lifetime received/sent and tx count." },
      { method: "GET", path: "/api/addresses/{address}/tx_count", desc: "Total number of transactions touching this address." },
      { method: "GET", path: "/api/addresses/{address}/utxos", desc: "Live UTXOs at this address.", paginated: true },
      { method: "GET", path: "/api/addresses/{address}/utxos/{asset}", desc: "Filter UTXOs by asset unit (use `lovelace` for ada-only).", paginated: true },
      { method: "GET", path: "/api/addresses/{address}/txs", desc: "Tx hashes (deprecated).", paginated: true, deprecated: true },
      { method: "GET", path: "/api/addresses/{address}/transactions", desc: "Transactions touching this address.", paginated: true, rangeFilter: true },
    ],
  },
  {
    group: "Cardano » Pools",
    identifier: "pool_id",
    endpoints: [
      { method: "GET", path: "/api/pools", desc: "List of all registered stake pool bech32 IDs.", paginated: true },
      { method: "GET", path: "/api/pools/extended", desc: "All pools with extended info — ticker, pledge, margin, live/active stake, blocks.", paginated: true },
      { method: "GET", path: "/api/pools/{pool_id}", desc: "Pool info — margin, pledge, live/active stake, delegators." },
      { method: "GET", path: "/api/pools/{pool_id}/history", desc: "Per-epoch active stake and delegator count.", paginated: true },
    ],
  },
  {
    group: "Cardano » Epochs",
    identifier: "epoch_number",
    endpoints: [
      { method: "GET", path: "/api/epochs/latest", desc: "Summary of the current epoch." },
      { method: "GET", path: "/api/epochs/latest/parameters", desc: "Protocol parameters active in the current epoch." },
      { method: "GET", path: "/api/epochs/{number}", desc: "Summary of a specific past or current epoch." },
      { method: "GET", path: "/api/epochs/{number}/blocks", desc: "Blocks produced during a specific epoch.", paginated: true },
    ],
  },
  {
    group: "Cardano » Transactions",
    identifier: "hash",
    endpoints: [
      { method: "GET", path: "/api/txs/recent", desc: "Most recent transactions across the chain, newest first.", paginated: true },
      { method: "GET", path: "/api/txs/{hash}", desc: "Transaction summary — block coords, fees, size, validity window, mint/deleg/cert counts." },
      { method: "GET", path: "/api/txs/{hash}/utxos", desc: "Full input and output list with native-asset bundles." },
      { method: "GET", path: "/api/txs/{hash}/metadata", desc: "Tx metadata entries (label, json_metadata, cbor_metadata)." },
    ],
  },
  {
    group: "Cardano » Assets",
    identifier: "asset",
    endpoints: [
      { method: "GET", path: "/api/assets", desc: "Paginated list of every native asset and its current live supply.", paginated: true },
      { method: "GET", path: "/api/assets/stats", desc: "Aggregate native-asset stats — total assets, policies and holder counts." },
      { method: "GET", path: "/api/assets/trending", desc: "Trending assets by activity over a window (`window=24h|7d`) — ident, unit, name, counts." },
      { method: "GET", path: "/api/assets/activity", desc: "Most active assets in a window — transfer / mint / burn counts (`window=24h|7d`)." },
      { method: "GET", path: "/api/assets/charts", desc: "Time-bucketed asset-activity series (blocks, active assets) for charts (`days`, `bucket`)." },
      { method: "GET", path: "/api/assets/recent-mints", desc: "Most recently minted assets, newest first." },
      { method: "GET", path: "/api/assets/{asset}", desc: "Asset detail — policy_id, asset_name, fingerprint, supply, initial mint tx, CIP-25/68 metadata." },
      { method: "GET", path: "/api/assets/{asset}/history", desc: "Mint and burn events for the asset.", paginated: true },
      { method: "GET", path: "/api/assets/{asset}/transactions", desc: "Transactions involving this asset (mint, burn or transfer).", paginated: true },
      { method: "GET", path: "/api/assets/{asset}/addresses", desc: "Current holders (live UTXO) ranked by quantity.", paginated: true },
      { method: "GET", path: "/api/assets/policy/{policy_id}", desc: "Every asset minted under a policy id.", paginated: true },
    ],
  },
  {
    group: "Cardano » Blocks",
    identifier: "hash_or_number",
    endpoints: [
      { method: "GET", path: "/api/blocks/latest", desc: "Current chain tip block — full detail." },
      { method: "GET", path: "/api/blocks/recent", desc: "Most recent blocks, newest first.", paginated: true },
      { method: "GET", path: "/api/blocks/latest/txs", desc: "Transaction hashes in the latest block.", paginated: true },
      { method: "GET", path: "/api/blocks/{hash_or_number}", desc: "Block by 64-char hex hash OR block_no (integer)." },
      { method: "GET", path: "/api/blocks/{hash_or_number}/next", desc: "List of subsequent blocks.", paginated: true },
      { method: "GET", path: "/api/blocks/{hash_or_number}/previous", desc: "List of preceding blocks.", paginated: true },
      { method: "GET", path: "/api/blocks/slot/{slot_number}", desc: "Block at the given absolute slot number." },
      { method: "GET", path: "/api/blocks/epoch/{epoch_number}/slot/{slot_number}", desc: "Block at the given epoch + epoch-slot." },
      { method: "GET", path: "/api/blocks/{hash_or_number}/txs", desc: "Tx hashes in this block.", paginated: true },
      { method: "GET", path: "/api/blocks/{hash_or_number}/addresses", desc: "Affected addresses in this block, with their tx hashes.", paginated: true },
    ],
  },
  {
    group: "Governance » Actions",
    endpoints: [
      { method: "GET", path: "/api/governance/proposals", desc: "All governance actions, newest-first; filter by `type` (info_action, parameter_change, treasury_withdrawals, hard_fork_initiation, new_committee, new_constitution, no_confidence) or `status`. Returns tx_hash, cert_index, gov_action_id, type, status, epochs and outcome.", paginated: true },
      { method: "GET", path: "/api/governance/proposals/live", desc: "Governance actions currently open for DRep / SPO / committee voting, with live vote_counts." },
      { method: "GET", path: "/api/governance/proposals/f6fd3678f12edc58dd8739560149fcdb0b8b6fc77a5f303d49c87c74d1fccb4c/0", desc: "Proposal detail {tx_hash}/{cert_index} — deposit, return_address, submitted/expiry epochs, metadata, anchor, vote_counts." },
      { method: "GET", path: "/api/governance/proposals/f6fd3678f12edc58dd8739560149fcdb0b8b6fc77a5f303d49c87c74d1fccb4c/0/votes", desc: "Individual votes cast on a proposal — voter_role, voter, vote, epoch, rationale anchor, tx.", paginated: true },
      { method: "GET", path: "/api/governance/proposals/f6fd3678f12edc58dd8739560149fcdb0b8b6fc77a5f303d49c87c74d1fccb4c/0/tally", desc: "Current tally per body (DRep, SPO, committee) — approval ratio, threshold, meets_threshold, voter counts." },
      { method: "GET", path: "/api/governance/proposals/f6fd3678f12edc58dd8739560149fcdb0b8b6fc77a5f303d49c87c74d1fccb4c/0/tally/history", desc: "Approval-ratio history across ratification check epochs." },
      { method: "GET", path: "/api/governance/proposals/f6fd3678f12edc58dd8739560149fcdb0b8b6fc77a5f303d49c87c74d1fccb4c/0/ratification", desc: "Ratification assessment — overall status plus per-body result, ratio and threshold." },
      { method: "GET", path: "/api/governance/proposals/f6fd3678f12edc58dd8739560149fcdb0b8b6fc77a5f303d49c87c74d1fccb4c/0/timeline", desc: "Lifecycle event log (submitted, voting, ratified / enacted / expired / dropped)." },
      { method: "GET", path: "/api/governance/proposals/75e7882a8ef2bc39517bffbfb654e89f525def5a8364d81e11fc5facafc6dd9b/0/parameter_changes", desc: "Proposed protocol-parameter changes (parameter_change actions) — parameter, group, security flag, guardrail script." },
      { method: "GET", path: "/api/governance/proposals/72491608dc1a500d38613214069a2bd3b7e9a0e670d5abd7d6374e6bb54fc0e8/0/withdrawals", desc: "Requested treasury withdrawals (treasury_withdrawals actions) — requested_total, recipients and amounts." },
    ],
  },
  {
    group: "Governance » Voting Bodies",
    endpoints: [
      { method: "GET", path: "/api/governance/dreps", desc: "All DReps ranked by voting power (incl. special always-abstain / no-confidence entries). Returns drep_id, status, voting_power, delegators_count, active_until.", paginated: true },
      { method: "GET", path: "/api/governance/dreps/drep1yfaaaaa270yjt6tu5skndugekprf5ykv5jshanl0c6gqx5qpstskf", desc: "DRep profile — ids (CIP-105/legacy/hex), status, voting power, delegators, deposit, registration, vote summary." },
      { method: "GET", path: "/api/governance/dreps/drep1yfaaaaa270yjt6tu5skndugekprf5ykv5jshanl0c6gqx5qpstskf/delegators", desc: "Stake accounts currently delegating to this DRep.", paginated: true },
      { method: "GET", path: "/api/governance/dreps/drep1yfaaaaa270yjt6tu5skndugekprf5ykv5jshanl0c6gqx5qpstskf/votes", desc: "Votes cast by this DRep, each linked to its governance action.", paginated: true },
      { method: "GET", path: "/api/governance/dreps/drep1yfaaaaa270yjt6tu5skndugekprf5ykv5jshanl0c6gqx5qpstskf/voting_power", desc: "Per-epoch voting-power history for this DRep." },
      { method: "GET", path: "/api/governance/committee", desc: "Current Constitutional Committee — members, quorum, no-confidence flag, enactment." },
      { method: "GET", path: "/api/governance/committee/history", desc: "Historical committee compositions and quorum thresholds." },
      { method: "GET", path: "/api/governance/committee/members/cc_cold1zwz2a08a8cqdp7r6lyv0cj67qqf47sr7x7vf8hm705ujc6s4m87eh", desc: "Committee member by cold credential — hot-key authorisation, status and memberships." },
      { method: "GET", path: "/api/governance/committee/members/cc_cold1zwz2a08a8cqdp7r6lyv0cj67qqf47sr7x7vf8hm705ujc6s4m87eh/votes", desc: "Votes cast by this committee member.", paginated: true },
      { method: "GET", path: "/api/governance/pools/pool1m83drqwlugdt9jn7jkz8hx3pne53acfkd539d9cj8yr92dr4k9y/votes", desc: "Governance votes cast by a stake pool (SPO), each linked to its action.", paginated: true },
      { method: "GET", path: "/api/governance/pools/pool1m83drqwlugdt9jn7jkz8hx3pne53acfkd539d9cj8yr92dr4k9y/voting_power", desc: "Per-epoch active-stake voting power for a stake pool." },
      { method: "GET", path: "/api/governance/voting_power", desc: "Aggregate governance voting power across DReps, SPOs and the committee." },
      { method: "GET", path: "/api/accounts/stake1u97v0sjx96u5lydjfe2g5qdwkj6plm87h80q5vc0ma6wjpq22mh4c/drep_delegations", desc: "DRep delegation history for a stake account.", paginated: true },
    ],
  },
  {
    group: "Governance » Treasury & Constitution",
    endpoints: [
      { method: "GET", path: "/api/governance/treasury", desc: "Per-epoch treasury & reserves balance with enacted withdrawals and proposal deposits." },
      { method: "GET", path: "/api/governance/parameters", desc: "Current protocol parameters — version, deposits, committee rules, DRep & SPO voting thresholds." },
      { method: "GET", path: "/api/governance/constitution", desc: "Current on-chain constitution — anchor document, data hash and guardrails script hash." },
      { method: "GET", path: "/api/governance/constitution/history", desc: "Every enacted constitution version over time." },
    ],
  },
  {
    group: "Cardano » Metadata",
    identifier: "label",
    endpoints: [
      { method: "GET", path: "/api/metadata/txs/labels", desc: "All transaction-metadata labels seen on-chain, with usage counts.", paginated: true },
      { method: "GET", path: "/api/metadata/txs/labels/{label}", desc: "Transactions carrying a specific metadata label.", paginated: true },
    ],
  },
  {
    group: "Service",
    endpoints: [
      { method: "GET", path: "/api/", desc: "Branded landing page (HTML)." },
      { method: "GET", path: "/api/info", desc: "Machine-readable service info (JSON)." },
      { method: "GET", path: "/api/network", desc: "Current Cardano chain tip." },
      { method: "GET", path: "/api/network/price", desc: "Live ADA market price (USD), market cap and 24h change (source: CoinGecko)." },
      { method: "GET", path: "/api/network/stats", desc: "Aggregate chain stats — pool count, total txs, active stake, rewards distributed." },
      { method: "GET", path: "/api/health", desc: "Lightweight health check." },
      { method: "GET", path: "/api/docs", desc: "Interactive Swagger UI." },
      { method: "GET", path: "/api/redoc", desc: "ReDoc reference." },
      { method: "GET", path: "/api/openapi.json", desc: "OpenAPI 3.1 schema." },
    ],
  },
];

// -------- small helpers --------
const methodStyles = {
  GET: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  POST: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  DELETE: "bg-red-500/10 text-red-400 border-red-500/30",
  PATCH: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
};

const resolvePath = (path, vars) =>
  path
    .replace("{stake_address}", vars.stake_address || "{stake_address}")
    .replace("{address}", vars.address || "{address}")
    .replace("{pool_id}", vars.pool_id || "{pool_id}")
    .replace("{policy_id}", vars.policy_id || "{policy_id}")
    .replace("{hash_or_number}", vars.hash_or_number || "latest")
    .replace("{slot_number}", vars.slot_number || "{slot_number}")
    .replace("{epoch_number}", vars.epoch_number || vars.number || "{epoch_number}")
    .replace("{number}", vars.number || "{number}")
    .replace("{hash}", vars.hash || "{hash}")
    .replace("{label}", vars.label || "674")
    .replace("{asset}", vars.asset || "lovelace");

const buildCurl = ({ path, backendUrl, projectId, vars, method }) => {
  const full = `${backendUrl}${resolvePath(path, vars)}`;
  const parts = ["curl", "-s"];
  if (method && method !== "GET") parts.push(`-X ${method}`);
  if (projectId) parts.push(`-H "x-project-id: ${projectId}"`);
  if (method === "POST") parts.push(`-H "Content-Type: application/json" -d '{}'`);
  parts.push(`"${full}"`);
  return parts.join(" ");
};

const SmallCopy = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid="drawer-copy-btn"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {}
      }}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground border border-border/60 px-2 py-1 transition-colors"
    >
      {copied ? <Check size={11} weight="bold" /> : <Copy size={11} />}
      {copied ? "copied" : "copy"}
    </button>
  );
};

// -------- endpoint row with try-it --------
const EndpointRow = ({ ep, backendUrl, projectId, vars, onVarChange, httpClient }) => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [count, setCount] = useState(5);
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState("desc");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  const usesStake = ep.path.includes("{stake_address}");
  const usesAddr = ep.path.includes("{address}");
  const usesAsset = ep.path.includes("{asset}");
  const usesPool = ep.path.includes("{pool_id}");
  const usesPolicy = ep.path.includes("{policy_id}");
  const usesBlock = ep.path.includes("{hash_or_number}");
  const usesSlot = ep.path.includes("{slot_number}");
  const usesEpoch = ep.path.includes("{epoch_number}");
  const usesNumber = ep.path.includes("{number}");
  const usesHash = ep.path.includes("{hash}");
  const usesLabel = ep.path.includes("{label}");

  const fullPath = useMemo(() => {
    let p = resolvePath(ep.path, vars);
    if (ep.paginated || ep.rangeFilter) {
      const qs = [];
      if (ep.paginated) {
        qs.push(`count=${count}`, `page=${page}`, `order=${order}`);
      }
      if (ep.rangeFilter) {
        if (rangeFrom) qs.push(`from=${rangeFrom}`);
        if (rangeTo) qs.push(`to=${rangeTo}`);
      }
      if (qs.length) p += `?${qs.join("&")}`;
    }
    return p;
  }, [ep, vars, count, page, order, rangeFrom, rangeTo]);

  const curl = buildCurl({
    path: fullPath,
    backendUrl,
    projectId,
    vars: {}, // already resolved
    method: ep.method,
  });

  const runIt = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    setRateLimited(false);
    try {
      const url = `${backendUrl}${fullPath}`;
      // Explicitly set the project_id headers here rather than relying on the
      // shared httpClient's default (which now points at the internal
      // explorer key) — "Try it" should reflect whatever identity the curl
      // example above shows (the visitor's own key, or the public demo key).
      const config = { headers: { "x-project-id": projectId, project_id: projectId } };
      let res;
      if (ep.method === "POST") {
        res = await httpClient.post(url, {}, config);
      } else if (ep.method === "DELETE") {
        res = await httpClient.delete(url, config);
      } else if (ep.method === "PATCH") {
        res = await httpClient.patch(url, {}, config);
      } else {
        res = await httpClient.get(url, config);
      }
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || { message: e.message });
      if (e.response?.status === 429) setRateLimited(true);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid={`drawer-ep-${ep.path}`}
      className="border border-border/60 bg-card/40"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/20 transition-colors"
      >
        {open ? (
          <CaretDown size={12} className="text-muted-foreground" />
        ) : (
          <CaretRight size={12} className="text-muted-foreground" />
        )}
        <span
          className={`mono text-[10px] uppercase tracking-[0.15em] border px-1.5 py-0.5 ${
            methodStyles[ep.method] || "border-border text-muted-foreground"
          }`}
        >
          {ep.method}
        </span>
        <code className="mono text-[12px] text-foreground flex-1 break-all">{ep.path}</code>
        {ep.deprecated ? (
          <span className="text-[9px] uppercase tracking-[0.2em] text-yellow-500/80">
            deprecated
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="border-t border-border/60 p-3 space-y-3 bg-background/60">
          <p className="text-[12px] text-muted-foreground">{ep.desc}</p>

          {/* Inline params */}
          {(usesStake || usesAddr || usesAsset || usesPool || usesPolicy || usesBlock || usesSlot || usesEpoch || usesNumber || usesHash || usesLabel || ep.paginated || ep.rangeFilter) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {usesStake ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  stake_address
                  <input
                    data-testid="drawer-var-stake"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.stake_address || ""}
                    onChange={(e) => onVarChange({ ...vars, stake_address: e.target.value })}
                  />
                </label>
              ) : null}
              {usesAddr ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  address
                  <input
                    data-testid="drawer-var-addr"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.address || ""}
                    onChange={(e) => onVarChange({ ...vars, address: e.target.value })}
                  />
                </label>
              ) : null}
              {usesPool ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  pool_id
                  <input
                    data-testid="drawer-var-pool"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.pool_id || ""}
                    onChange={(e) => onVarChange({ ...vars, pool_id: e.target.value })}
                    placeholder="pool1…"
                  />
                </label>
              ) : null}
              {usesPolicy ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  policy_id
                  <input
                    data-testid="drawer-var-policy"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.policy_id || ""}
                    onChange={(e) => onVarChange({ ...vars, policy_id: e.target.value })}
                    placeholder="56-char hex policy id"
                  />
                </label>
              ) : null}
              {usesBlock ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  hash_or_number
                  <input
                    data-testid="drawer-var-block"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.hash_or_number || ""}
                    onChange={(e) => onVarChange({ ...vars, hash_or_number: e.target.value })}
                    placeholder="block_no or 64-char hash"
                  />
                </label>
              ) : null}
              {usesSlot ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  slot_number
                  <input
                    data-testid="drawer-var-slot"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.slot_number || ""}
                    onChange={(e) => onVarChange({ ...vars, slot_number: e.target.value })}
                    placeholder="absolute or epoch slot"
                  />
                </label>
              ) : null}
              {usesEpoch ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  epoch_number
                  <input
                    data-testid="drawer-var-epoch"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.epoch_number || vars.number || ""}
                    onChange={(e) => onVarChange({ ...vars, epoch_number: e.target.value })}
                    placeholder="e.g. 285"
                  />
                </label>
              ) : null}
              {usesNumber ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  epoch number
                  <input
                    data-testid="drawer-var-number"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.number || ""}
                    onChange={(e) => onVarChange({ ...vars, number: e.target.value })}
                    placeholder="e.g. 284"
                  />
                </label>
              ) : null}
              {usesHash ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  tx hash
                  <input
                    data-testid="drawer-var-hash"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.hash || ""}
                    onChange={(e) => onVarChange({ ...vars, hash: e.target.value })}
                    placeholder="64-char lower-case hex"
                  />
                </label>
              ) : null}
              {usesLabel ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  metadata label
                  <input
                    data-testid="drawer-var-label"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.label || ""}
                    onChange={(e) => onVarChange({ ...vars, label: e.target.value })}
                    placeholder="e.g. 674"
                  />
                </label>
              ) : null}
              {usesAsset ? (
                <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  asset (unit or `lovelace`)
                  <input
                    data-testid="drawer-var-asset"
                    className="mono tracking-normal normal-case text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    value={vars.asset}
                    onChange={(e) => onVarChange({ ...vars, asset: e.target.value })}
                  />
                </label>
              ) : null}
              {ep.paginated ? (
                <div className="flex gap-2 items-end">
                  <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex-1">
                    count
                    <input
                      type="number"
                      min={1}
                      max={100}
                      className="mono tracking-normal text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                      value={count}
                      onChange={(e) => setCount(Number(e.target.value))}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex-1">
                    page
                    <input
                      type="number"
                      min={1}
                      className="mono tracking-normal text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                      value={page}
                      onChange={(e) => setPage(Number(e.target.value))}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    order
                    <select
                      value={order}
                      onChange={(e) => setOrder(e.target.value)}
                      className="mono tracking-normal text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                    >
                      <option value="asc">asc</option>
                      <option value="desc">desc</option>
                    </select>
                  </label>
                </div>
              ) : null}
              {ep.rangeFilter ? (
                <div className="flex gap-2 items-end">
                  <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex-1">
                    from (block[:idx])
                    <input
                      className="mono tracking-normal text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                      value={rangeFrom}
                      onChange={(e) => setRangeFrom(e.target.value)}
                      placeholder="e.g. 3000000"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex-1">
                    to
                    <input
                      className="mono tracking-normal text-[12px] bg-card border border-border/70 px-2 py-1.5 text-foreground"
                      value={rangeTo}
                      onChange={(e) => setRangeTo(e.target.value)}
                      placeholder="e.g. 3100000"
                    />
                  </label>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* cURL snippet */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">curl</span>
              <div className="flex items-center gap-2">
                <SmallCopy value={curl} />
                <button
                  data-testid="drawer-run-btn"
                  onClick={runIt}
                  disabled={running}
                  className="inline-flex items-center gap-1.5 border border-primary/60 bg-primary/10 text-primary px-3 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-primary/20 disabled:opacity-60 transition-colors"
                >
                  <Play size={11} weight="fill" /> {running ? "running…" : "try it"}
                </button>
              </div>
            </div>
            <pre className="mono text-[11px] bg-card/80 border border-border/60 p-2.5 overflow-x-auto text-foreground/90">
{curl}
            </pre>
          </div>

          {/* Result */}
          {error ? (
            <div className="border border-red-500/40 bg-red-500/5 text-red-400 p-2.5 text-[11px] mono">
              {error.status_code ? `${error.status_code} ` : ""}
              {error.error ? `${error.error}: ` : ""}
              {error.message || "Request failed"}
            </div>
          ) : null}
          {rateLimited ? (
            <div
              data-testid="drawer-rate-limit-banner"
              className="flex items-center justify-between gap-3 border border-amber-500/40 bg-amber-500/10 text-amber-300 p-2.5 text-[11px]"
            >
              <span>
                You've hit the demo key's rate limit. Get your own free API key to keep
                exploring without limits.
              </span>
              <a
                href="https://gridscan.io/dashboard"
                target="_blank"
                rel="noreferrer"
                data-testid="drawer-rate-limit-signup-btn"
                className="shrink-0 inline-flex items-center gap-1.5 border border-amber-400/60 bg-amber-400/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] hover:bg-amber-400/20 transition-colors whitespace-nowrap"
              >
                Get a free key
              </a>
            </div>
          ) : null}
          {result !== null ? (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">response</span>
                <SmallCopy value={JSON.stringify(result, null, 2)} />
              </div>
              <pre className="mono text-[11px] bg-card/80 border border-border/60 p-2.5 overflow-auto max-h-[260px] text-foreground/90">
{JSON.stringify(result, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// -------- drawer --------
export const ApiReferenceDrawer = ({
  open,
  onClose,
  backendUrl,
  projectId,
  httpClient,
  defaults, // { stake_address, address, asset }
}) => {
  const [filter, setFilter] = useState("");
  const [vars, setVars] = useState(defaults || {});

  const totalEndpoints = useMemo(
    () => ENDPOINT_GROUPS.reduce((sum, g) => sum + g.endpoints.length, 0),
    [],
  );

  const filtered = useMemo(() => {
    if (!filter.trim()) return ENDPOINT_GROUPS;
    const f = filter.toLowerCase();
    return ENDPOINT_GROUPS.map((g) => ({
      ...g,
      endpoints: g.endpoints.filter(
        (e) => e.path.toLowerCase().includes(f) || e.desc.toLowerCase().includes(f),
      ),
    })).filter((g) => g.endpoints.length > 0);
  }, [filter]);

  return (
    <>
      {/* backdrop */}
      <div
        data-testid="drawer-backdrop"
        onClick={onClose}
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[65] transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* panel */}
      <aside
        data-testid="api-drawer"
        className={`fixed top-0 right-0 bottom-0 w-full md:w-[640px] lg:w-[760px] z-[70] bg-background border-l border-border/70 shadow-2xl transform transition-transform flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="px-5 py-4 border-b border-border/70 flex items-center justify-between sticky top-0 bg-background z-10 shrink-0">
          <div className="flex items-center gap-3">
            <BookOpen size={16} className="text-primary" />
            <div>
              <div className="text-sm font-semibold tracking-tight">API Reference</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {totalEndpoints} endpoints · {CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod testnet"}
              </div>
            </div>
          </div>
          <button
            data-testid="drawer-close"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center border border-border/70 hover:bg-muted/30 transition-colors"
          >
            <X size={14} />
          </button>
        </header>

        <div
          data-testid="drawer-get-key-banner"
          className="px-5 py-2.5 border-b border-border/70 bg-primary/5 flex items-center justify-between gap-3 flex-wrap shrink-0"
        >
          <span className="text-[11px] text-muted-foreground">
            New here? Get your own free API key — skip the shared demo rate limit.
          </span>
          <a
            href="https://gridscan.io/dashboard"
            target="_blank"
            rel="noreferrer"
            data-testid="drawer-get-key-link"
            className="shrink-0 inline-flex items-center gap-1.5 border border-primary/50 bg-primary/10 text-primary px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] hover:bg-primary/20 transition-colors"
          >
            Get a free key
          </a>
        </div>

        <div className="px-5 py-3 border-b border-border/70 flex items-center gap-2 bg-card/40 shrink-0">
          <MagnifyingGlass size={13} className="text-muted-foreground" />
          <input
            data-testid="drawer-filter"
            placeholder="Filter endpoints…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 bg-transparent outline-none text-[12px] mono text-foreground placeholder:text-muted-foreground/60"
          />
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-6">
          {filtered.map((group) => (
            <section key={group.group}>
              <h3 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2 font-sans">
                {group.group}
              </h3>
              <div className="space-y-2">
                {group.endpoints.map((ep) => (
                  <EndpointRow
                    key={`${ep.method}:${ep.path}`}
                    ep={ep}
                    backendUrl={backendUrl}
                    projectId={projectId}
                    vars={vars}
                    onVarChange={setVars}
                    httpClient={httpClient}
                  />
                ))}
              </div>
            </section>
          ))}
          <div className="pt-4 border-t border-border/70 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Full OpenAPI schema:{" "}
            <a
              href={`${backendUrl}/api/openapi.json`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline normal-case tracking-normal mono"
            >
              {backendUrl}/api/openapi.json
            </a>
          </div>
        </div>
      </aside>
    </>
  );
};
